/**
 * Uploads an image as a template asset and inserts an `image` node at `pos`. Shared by the
 * toolbar's ImageBtn and the Blocks palette's Image block so both produce the same node.
 * `pos` must be resolved by the caller before the upload starts — by the time it resolves the
 * selection may have moved on. With `replaceEmpty`, `pos` is the start of an empty paragraph the
 * image takes the place of, if it is still empty.
 */
import type { Editor } from '@tiptap/core'
import { templatesApiService } from '../../services/templatesApiService'

export function uploadAndInsertImage(editor: Editor, templateId: string, file: File, pos: number, altText = '', replaceEmpty = false): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new window.Image()
    const objectUrl = URL.createObjectURL(file)
    img.onerror = () => reject(new Error('Not a readable image'))
    img.onload = async () => {
      try {
        const { blobRef } = await templatesApiService.uploadAsset(templateId, file)
        const widthPt = Math.round(img.naturalWidth * 0.75)
        const heightPt = Math.round(img.naturalHeight * 0.75)
        const alt = altText.trim() || null
        // Re-checked now: the paragraph may have been typed into while the upload ran.
        const target = editor.state.doc.nodeAt(pos)
        const at = replaceEmpty && target?.type.name === 'paragraph' && target.content.size === 0
          ? { from: pos, to: pos + target.nodeSize }
          : pos
        editor.chain().focus().insertContentAt(at, {
          type: 'image',
          attrs: { blobRef, widthPt, heightPt, altText: alt, sourceMoniker: null, resolvedSource: objectUrl, src: objectUrl, alt },
        }).run()
        resolve()
      } catch (err) {
        reject(err)
      }
    }
    img.src = objectUrl
  })
}
